import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { OrganizationSchema, WebSiteSchema, WebPageSchema } from '@/components/base/SEOSchemas';
import { comics, comicCategories, reviews, platformStats } from '@/mocks/comics';

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const featuredComics = useMemo(() => comics.filter((c) => c.rating >= 9.5).slice(0, 3), []);
  const latestComics = useMemo(() => [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 8), []);
  const popularComics = useMemo(() => [...comics].sort((a, b) => b.views - a.views).slice(0, 6), []);
  const filteredByCategory = useMemo(() => {
    if (activeTab === 'all') return comics.slice(0, 6);
    return comics.filter((c) => c.category === activeTab).slice(0, 6);
  }, [activeTab]);

  return (
    <>
      <WebSiteSchema />
      <OrganizationSchema />
      <WebPageSchema
        name="飞飞漫画网 - 最全方便的韩国漫画平台"
        description="飞飞漫画网官网首页是最全且方便的韩国漫画平台，资源整合度高，浏览体验流畅，设计人性化，助您轻松访问海量漫画内容。"
        url="/"
      />

      <Navbar />

      <main>
        {/* ========== HERO SECTION ========== */}
        <section className="relative h-[520px] md:h-[640px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20stunning%20artistic%20Korean%20manhwa%20style%20illustration%20featuring%20multiple%20characters%20from%20different%20genres%20all%20gathered%20together%2C%20vibrant%20yet%20soft%20colors%20with%20warm%20coral%20pink%20and%20golden%20amber%20accents%2C%20dreamy%20bokeh%20background%2C%20floating%20magical%20sparkles%2C%20dramatic%20yet%20gentle%20lighting%2C%20rich%20texture%2C%20cinematic%20composition%2C%20no%20text%2C%20anime%20art%20style%20with%20painterly%20feel%2C%20seamless%20character%20arrangement%20across%20the%20canvas&width=1920&height=1080&seq=hero-feifei-2026&orientation=landscape"
            alt="飞飞漫画网 - 韩国漫画平台"
            title="飞飞漫画网 - 最全方便的韩国漫画平台"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/55"></div>

          <div className="relative z-10 w-full h-full flex flex-col items-start justify-center px-4 md:px-6">
            <div className="w-full max-w-7xl mx-auto">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold font-heading text-white leading-tight mb-4 max-w-2xl">
                最全方便的
                <br />
                韩国漫画平台
              </h1>
              <p className="text-base md:text-lg text-white/70 max-w-xl mb-8 leading-relaxed">
                海量韩国漫画资源，流畅浏览体验，人性化设计。飞飞漫画网助您轻松畅享精彩韩漫世界。
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                <Link
                  to="/category/all"
                  className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-7 py-3 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer"
                >
                  开始阅读
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/about"
                  className="whitespace-nowrap inline-flex items-center gap-2 border border-white/40 hover:border-white/80 text-white px-7 py-3 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div className="absolute bottom-8 left-0 right-0 z-10">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-8 md:gap-16">
                <div>
                  <span className="block text-2xl md:text-3xl font-bold text-white">{((platformStats.totalComics / 10000).toFixed(1))}万+</span>
                  <span className="text-xs md:text-sm text-white/60">漫画资源</span>
                </div>
                <div>
                  <span className="block text-2xl md:text-3xl font-bold text-white">{((platformStats.totalUsers / 10000).toFixed(0))}万+</span>
                  <span className="text-xs md:text-sm text-white/60">注册用户</span>
                </div>
                <div>
                  <span className="block text-2xl md:text-3xl font-bold text-white">{((platformStats.dailyActive / 10000).toFixed(0))}万+</span>
                  <span className="text-xs md:text-sm text-white/60">日活跃用户</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========== FEATURED SECTION ========== */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="w-full max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                热门推荐
              </h2>
              <p className="text-sm text-foreground-500">精选高分韩漫，不容错过的精彩作品</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} variant="featured" />
              ))}
            </div>
          </div>
        </section>

        {/* ========== LATEST UPDATES + CATEGORIES ========== */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="w-full max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              {/* Left - Latest Updates */}
              <div className="lg:w-3/5">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                      最近更新
                    </h2>
                    <p className="text-sm text-foreground-500 mt-1">实时追踪最新话更新</p>
                  </div>
                  <Link
                    to="/category/all"
                    className="whitespace-nowrap text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    查看全部 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="space-y-1">
                  {latestComics.map((comic) => (
                    <ComicCard key={`latest-${comic.id}`} comic={comic} variant="default" />
                  ))}
                </div>
              </div>

              {/* Right - Popular Ranking */}
              <div className="lg:w-2/5">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                      人气排行
                    </h2>
                    <p className="text-sm text-foreground-500 mt-1">最受欢迎的韩漫作品</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {popularComics.map((comic, index) => (
                    <Link
                      key={`pop-${comic.id}`}
                      to={`/comic/${comic.id}`}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-background-50 transition-colors cursor-pointer group"
                    >
                      <span className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
                        index < 3 ? 'bg-primary-500 text-white' : 'bg-background-200 text-foreground-500'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {comic.title}
                        </h3>
                        <p className="text-xs text-foreground-500">{comic.author}</p>
                      </div>
                      <span className="text-xs text-primary-500 font-medium whitespace-nowrap">{comic.rating}分</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========== CATEGORY BROWSING ========== */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="w-full max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                分类浏览
              </h2>
              <p className="text-sm text-foreground-500">按你喜欢的类型探索更多漫画</p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center justify-center flex-wrap gap-2 mb-10">
              {comicCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.slug)}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Category Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredByCategory.map((comic) => (
                <ComicCard key={`cat-${comic.id}`} comic={comic} variant="compact" />
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to={`/category/${activeTab}`}
                className="whitespace-nowrap inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer"
              >
                浏览更多 {comicCategories.find((c) => c.slug === activeTab)?.name || ''} 漫画
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* ========== ADVANTAGE SECTION ========== */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="w-full max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              {/* Left - Text */}
              <div className="lg:w-1/2">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                  为什么选择
                  <br />
                  飞飞漫画网？
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8 max-w-md">
                  我们深知漫画爱好者的需求。飞飞漫画网以极致的用户体验为核心，为您打造最舒适、最高效的在线阅读环境。海量资源、光速更新、纯净无广告，让阅读回归纯粹的快乐。
                </p>
                <Link
                  to="/about"
                  className="whitespace-nowrap inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer"
                >
                  了解更多 <i className="ri-arrow-right-line"></i>
                </Link>

                <div className="flex items-center gap-12 mt-12">
                  <div>
                    <span className="block text-3xl md:text-4xl font-bold text-foreground-950">{((platformStats.totalComics / 10000).toFixed(1))}万+</span>
                    <span className="text-xs text-foreground-500">漫画资源</span>
                  </div>
                  <div>
                    <span className="block text-3xl md:text-4xl font-bold text-foreground-950">{((platformStats.totalViews / 100000000).toFixed(1))}亿+</span>
                    <span className="text-xs text-foreground-500">累计阅读量</span>
                  </div>
                </div>
              </div>

              {/* Right - Image */}
              <div className="lg:w-1/2">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20manhwa%20style%20digital%20illustration%20showing%20a%20cozy%20reading%20scene%20on%20a%20tablet%20device%2C%20surrounded%20by%20warm%20ambient%20lighting%2C%20soft%20blanket%2C%20hot%20drink%2C%20evening%20mood%2C%20chill%20vibes%2C%20anime%20art%20style%20with%20soft%20texture%2C%20warm%20coral%20and%20cream%20tones%2C%20peaceful%20atmosphere%2C%20no%20text%20on%20screen&width=1200&height=800&seq=advantage-feifei&orientation=landscape"
                    alt="飞飞漫画网阅读体验"
                    title="飞飞漫画网 - 极致阅读体验"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-foreground-800 text-xs px-3 py-1.5 rounded-full font-medium">
                    极致阅读体验
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-5">
                    <p className="text-white text-sm font-medium">随时随地，畅享漫画世界</p>
                    <p className="text-white/70 text-xs mt-1">PC · 平板 · 手机 全平台适配</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========== REVIEWS SECTION ========== */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="w-full max-w-7xl mx-auto">
            <div className="mb-4">
              <span className="inline-block bg-accent-100 text-accent-800 text-xs px-3 py-1 rounded-full font-medium">
                用户口碑
              </span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
              数百万读者的
              <br />
              共同选择
            </h2>
            <p className="text-sm text-foreground-500 mb-12">听听他们怎么说</p>

            {/* Masonry Grid */}
            <div className="columns-1 md:columns-2 lg:columns-3 gap-5">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="break-inside-avoid mb-5 bg-background-100 rounded-lg p-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {review.avatar}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-400">@user_{review.id} · {review.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    &ldquo;{review.comment}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========== CTA SECTION ========== */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="w-full max-w-3xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=A%20beautiful%20wide%20Korean%20manhwa%20style%20banner%20illustration%20with%20artistic%20film%20grain%20texture%2C%20featuring%20multiple%20manga%20characters%20silhouettes%20in%20dynamic%20poses%2C%20warm%20coral%20and%20cream%20gradient%20background%2C%20soft%20dreamy%20atmosphere%2C%20anime%20art%20style%2C%20ethereal%20lighting%2C%20cinematic%20wide%20composition%2C%20no%20text&width=1600&height=600&seq=cta-feifei-2026&orientation=landscape"
                alt="加入飞飞漫画网"
                title="飞飞漫画网 - 开启漫画之旅"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3 leading-snug">
                加入<span className="font-body text-foreground-500 font-normal italic mx-1">数百万</span>读者
                <br />
                开启你的<span className="font-body text-foreground-500 font-normal italic mx-1">韩漫</span>之旅
              </h2>
              <p className="text-sm text-foreground-500 mb-8">免费注册，即刻畅享海量优质漫画内容</p>
              <Link
                to="/category/all"
                className="whitespace-nowrap inline-flex items-center gap-2 bg-foreground-950 hover:bg-foreground-800 text-white px-8 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer"
              >
                立即开始阅读
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}